<template>
    <v-alert
        v-if="displayAlert"
        :type="alertMeta.type"
        :class="alertClass ?? 'px-4 py-2'"
        :icon="alertMeta.icon"
        :data-cy="cypressData"
        density="compact"
        rounded="lg"
        :closable="
            onboardingStatus ===
            OnboardingVerificationStatus.VERIFICATION_COMPLETE
        "
    >
        <template #title>
            <p class="body-medium font-weight-bold">
                {{ alertMeta.title }}
            </p>
        </template>
        <p class="body-medium">
            {{ alertMeta.message }}
            <span
                v-if="alertMeta.ctaLabel"
                class="default-text-button font-weight-medium body-medium"
                @click="
                    redirectToRoute({
                        name: OrganisationRouteNames.CARD_PROVIDER_ONBOARDING,
                    })
                "
                >{{ alertMeta.ctaLabel }}</span
            >
        </p>
    </v-alert>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { usePiniaStore } from "@/stores/store";

import { redirectToRoute } from "@/router";
import { OrganisationRouteNames } from "@/constants/routes";
import { OnboardingVerificationStatus } from "@/constants/compliance";
import {
    mdiAlertCircleOutline,
    mdiCheckCircleOutline,
    mdiClockOutline,
    mdiInformationOutline,
} from "@mdi/js";

defineProps<{
    cypressData?: string;
    alertClass?: string;
}>();

const pStore = usePiniaStore();

/**
 * Determines whether to display the alert based on the presence of onboarding status.
 * If the onboarding status is defined, the alert will be displayed.
 * If the onboarding status is VERIFICATION_COMPLETE or ONBOARDING_COMPLETE, the alert will not be displayed as
 * there is no action required.
 * @return {boolean} True if the alert should be displayed, false otherwise.
 */
const displayAlert = computed(() => {
    const schoolDetails = pStore.getSchoolSettings;
    return (
        schoolDetails?.adyen_onboarding_status != null &&
        ![
            OnboardingVerificationStatus.VERIFICATION_COMPLETE,
            OnboardingVerificationStatus.ONBOARDING_COMPLETE,
        ].includes(schoolDetails.adyen_onboarding_status)
    );
});

// Onboarding status from the store
const onboardingStatus = computed(() => {
    const schoolDetails = pStore.getSchoolSettings;
    return schoolDetails?.adyen_onboarding_status as OnboardingVerificationStatus;
});

type AlertType = "success" | "info" | "warning" | "error";
interface AlertMeta {
    icon: string;
    type: AlertType;
    title: string;
    message: string;
    ctaLabel: string | null;
}

/**
 * Computes the alert metadata based on the onboarding status.
 * This includes the icon, type, title, message, and call-to-action label.
 * @return {AlertMeta} The alert metadata corresponding to the onboarding status.
 */
const alertMeta = computed<AlertMeta>(() => {
    switch (onboardingStatus.value) {
        case OnboardingVerificationStatus.VERIFICATION_COMPLETE:
            return {
                icon: mdiCheckCircleOutline,
                type: "success",
                title: "Verification Complete",
                message:
                    "Your account has been successfully verified. You are all set.",
                ctaLabel: null,
            };
        case OnboardingVerificationStatus.MORE_INFORMATION_REQUIRED:
            return {
                icon: mdiAlertCircleOutline,
                type: "warning",
                title: "More Information Required",
                message:
                    "Additional information is required to complete your verification. Please update your details to continue.",
                ctaLabel: "Update Information",
            };
        case OnboardingVerificationStatus.PENDING_VERIFICATION:
            return {
                icon: mdiClockOutline,
                type: "warning",
                title: "Verification in Progress",
                message:
                    "Your submitted information is being reviewed. This process may take some time.",
                ctaLabel: "View Status",
            };
        default:
            return {
                icon: mdiInformationOutline,
                type: "warning",
                title: "Get Started",
                message:
                    "Further information is required. Please complete the onboarding process.",
                ctaLabel: "Start Onboarding",
            };
    }
});
</script>
